import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WhatsappLogo, PaperPlaneTilt, ShieldCheck, CaretLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

function errText(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, requestOtp, verifyOtp } = useAuth();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("91");
  const [otp, setOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (user) return <Navigate to="/" replace />;

  const sendOtp = async (e) => {
    if (e) e.preventDefault();
    const cleanPhone = phone.replace(/\D/g, "");
    if (cleanPhone.length < 10) {
      toast.error("Valid phone number daaliye");
      return;
    }
    setSubmitting(true);
    try {
      await requestOtp(cleanPhone);
      toast.success("OTP bheja gaya WhatsApp par");
      setStep("otp");
      setCooldown(60);
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (otp.length < 4) {
      toast.error("6-digit OTP daaliye");
      return;
    }
    setSubmitting(true);
    try {
      await verifyOtp(phone, otp);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full w-full grid md:grid-cols-2 bg-[var(--bg)] text-[var(--fg)]">
      {/* Left: brand pane */}
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[#25D366] grid place-items-center">
            <WhatsappLogo weight="fill" size={20} />
          </div>
          <div>
            <div className="heading font-bold text-lg leading-tight">RodBez Operation</div>
            <div className="text-xs text-white/60 leading-tight">Driver inbox · WhatsApp + MongoDB</div>
          </div>
        </div>

        <div>
          <div className="heading text-4xl font-bold leading-tight tracking-tight">
            One inbox<br />for your whole team.
          </div>
          <div className="text-white/60 text-sm mt-5 max-w-sm leading-relaxed">
            Secure OTP login via WhatsApp. No passwords, no hassle — just a code on your phone.
          </div>
          <div className="flex gap-2 mt-8">
            {["WhatsApp OTP", "No passwords", "Secure", "Fast"].map((b) => (
              <span key={b} className="text-[11px] tracking-wider uppercase border border-white/20 rounded-full px-3 py-1 text-white/70">
                {b}
              </span>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-white/40">© 2026 RodBez</div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-5">
          <div className="md:hidden flex items-center gap-2 mb-6">
            <div className="h-8 w-8 rounded-md bg-[#0A0A0A] grid place-items-center text-white">
              <WhatsappLogo weight="fill" size={16} />
            </div>
            <div className="heading font-semibold">RodBez Operation</div>
          </div>

          {step === "phone" ? (
            <form onSubmit={sendOtp} className="space-y-5" data-testid="login-phone-form">
              <div>
                <div className="heading text-2xl font-bold tracking-tight">Sign in</div>
                <div className="text-sm text-[var(--muted)] mt-1">
                  Apna registered phone number daaliye — OTP WhatsApp par aayega.
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone number</Label>
                <div className="flex gap-2 items-stretch">
                  <div className="flex items-center px-3 rounded-md border border-[var(--border)] bg-[var(--bg)] text-sm text-[var(--muted)] font-mono">
                    +91
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    placeholder="98XX XXXXXX"
                    value={phone.startsWith("91") ? phone.slice(2) : phone}
                    onChange={(e) => {
                      const d = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setPhone("91" + d);
                    }}
                    autoComplete="tel"
                    inputMode="numeric"
                    className="flex-1"
                    data-testid="login-phone-input"
                  />
                </div>
                <div className="text-[11px] text-[var(--muted)]">
                  Apna 10-digit Indian mobile number daaliye.
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#0A0A0A] hover:bg-black gap-2"
                disabled={submitting}
                data-testid="login-send-otp-btn"
              >
                <PaperPlaneTilt size={16} weight="fill" />
                {submitting ? "Sending OTP…" : "Send OTP on WhatsApp"}
              </Button>

              <div className="text-xs text-[var(--muted)] text-center">
                Team members? Ask your admin to register your phone.
              </div>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-5" data-testid="login-otp-form">
              <div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("phone");
                      setOtp("");
                    }}
                    className="p-1 -ml-1 hover:bg-[var(--bg)] rounded"
                    data-testid="login-back-to-phone"
                  >
                    <CaretLeft size={18} weight="bold" />
                  </button>
                  <div className="heading text-2xl font-bold tracking-tight">Enter OTP</div>
                </div>
                <div className="text-sm text-[var(--muted)] mt-1">
                  6-digit code WhatsApp par bheja gaya hai <span className="font-mono">+{phone}</span> par.
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="otp">OTP</Label>
                <Input
                  id="otp"
                  type="tel"
                  required
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={8}
                  className="tracking-[0.4em] text-center font-mono text-lg"
                  data-testid="login-otp-input"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#0A0A0A] hover:bg-black gap-2"
                disabled={submitting}
                data-testid="login-verify-otp-btn"
              >
                <ShieldCheck size={16} weight="fill" />
                {submitting ? "Verifying…" : "Verify & Sign in"}
              </Button>

              <div className="text-center text-xs text-[var(--muted)]">
                {cooldown > 0 ? (
                  <span>Resend OTP in {cooldown}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => sendOtp()}
                    className="underline hover:text-[var(--fg)]"
                    data-testid="login-resend-otp-btn"
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
